import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ClipboardList, Clock, FileText, Layers, Plus } from "lucide-react";
import { useAuth } from "../auth/AuthContext.js";
import { apiGet } from "../lib/api.js";
import { StatCard } from "../components/StatCard.js";
import { Panel } from "../components/Panel.js";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { Button } from "../components/Button.js";
import { StatusPill } from "../components/StatusPill.js";

interface FormSummary {
  id: string;
  title: string;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  submissionCount: number;
  closesAt: string | null;
  updatedAt: string;
}

const relativeTime = (iso: string): string => {
  const diffMs = Date.parse(iso) - Date.now();
  const days = Math.round(diffMs / 86_400_000);
  if (days === 0) return "today";
  if (days > 0) return `in ${days} day${days === 1 ? "" : "s"}`;
  return `${-days} day${days === -1 ? "" : "s"} ago`;
};

/**
 * The landing screen after sign-in. Every number here is a real query against what the caller can
 * already see (`GET /forms`, `GET /programmes`) — no separate summary endpoint, since a dashboard
 * home composing from existing list endpoints is one fewer thing that can drift out of sync with
 * the screens it links to.
 */
export const DashboardHome = ({
  onOpenForm,
  onOpenForms,
  onNewForm,
}: {
  /** Opens the builder for one form — the panels below were previously dead text. */
  onOpenForm: (formId: string) => void;
  onOpenForms: () => void;
  onNewForm: () => void;
}): JSX.Element => {
  const { user } = useAuth();

  const formsQuery = useQuery({
    queryKey: ["forms"],
    queryFn: () => apiGet<{ data: FormSummary[] }>("/forms"),
  });

  const programmesQuery = useQuery({
    queryKey: ["programmes"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/programmes"),
    retry: false,
  });

  const forms = formsQuery.data?.data ?? [];
  const published = forms.filter((f) => f.status === "PUBLISHED");
  const totalSubmissions = forms.reduce((sum, f) => sum + f.submissionCount, 0);
  const closingSoon = published
    .filter((f) => f.closesAt && Date.parse(f.closesAt) - Date.now() < 14 * 86_400_000)
    .sort((a, b) => Date.parse(a.closesAt ?? "") - Date.parse(b.closesAt ?? ""));
  const recent = [...forms]
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, 5);

  const noFormsYet = !formsQuery.isLoading && forms.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Welcome, ${user?.fullName ?? ""}`.trim()}
        description={
          user?.isSuperadmin
            ? "You have superadmin access across every programme."
            : `You have access to ${user?.programmeRoles.length ?? 0} programme(s).`
        }
        actions={
          <Button variant="primary" icon={Plus} onClick={onNewForm}>
            New form
          </Button>
        }
      />

      {/* First run. Four KPI tiles all reading zero explain nothing to someone who has just signed
          in to an empty platform; this says what to do instead. */}
      {noFormsYet ? (
        <EmptyState
          illustration="/illustrations/empty-explore.webp"
          icon={FileText}
          title="Nothing here yet — that is expected"
          description="Start by creating a form. Add its questions in the builder, publish it, and you get a public link to share with applicants. Submissions and analytics fill in from there."
          action={
            <Button variant="primary" icon={Plus} onClick={onNewForm}>
              Create the first form
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Programmes"
              value={programmesQuery.data?.data.length ?? "—"}
              icon={Layers}
              tone="primary"
            />
            <StatCard
              label="Published forms"
              value={published.length}
              icon={CheckCircle2}
              tone="accent"
              onClick={onOpenForms}
            />
            <StatCard
              label="Total submissions"
              value={totalSubmissions.toLocaleString()}
              icon={ClipboardList}
              tone="primary"
              onClick={onOpenForms}
            />
            <StatCard
              label="Closing within 14 days"
              value={closingSoon.length}
              icon={Clock}
              tone={closingSoon.length > 0 ? "destructive" : "muted"}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel
              title="Recently updated forms"
              tools={
                <Button size="sm" variant="ghost" onClick={onOpenForms}>
                  View all
                </Button>
              }
            >
              {recent.length === 0 ? (
                <EmptyState
                  compact
                  icon={FileText}
                  title="No forms yet"
                  description="Forms you create or edit will appear here, most recent first."
                />
              ) : (
                <ul className="divide-y">
                  {recent.map((form) => (
                    <li key={form.id}>
                      <button
                        type="button"
                        onClick={() => onOpenForm(form.id)}
                        className="flex w-full items-center gap-3 py-2 text-left hover:text-primary"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {form.title}
                        </span>
                        <StatusPill status={form.status} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Closing soon" subtitle="Published forms closing within 14 days">
              {closingSoon.length === 0 ? (
                <EmptyState
                  compact
                  icon={Clock}
                  title="Nothing closing soon"
                  description="A published form with a closing date inside the next two weeks shows up here, so a deadline does not arrive unnoticed."
                />
              ) : (
                <ul className="divide-y">
                  {closingSoon.map((form) => (
                    <li key={form.id}>
                      <button
                        type="button"
                        onClick={() => onOpenForm(form.id)}
                        className="flex w-full items-center gap-3 py-2 text-left hover:text-primary"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {form.title}
                        </span>
                        <span className="shrink-0 text-xs font-medium text-destructive">
                          closes {relativeTime(form.closesAt!)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
};
